'use client'

import React from 'react'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { AnimatedGroup } from '@/components/ui/animated-group'
import { HeroHeader } from '@/components/header'
import Footer from '@/components/footer'
import { 
  User, 
  Target, 
  Lightbulb,
  Heart,
  Code,
  ArrowRight,
  Coffee,
  Zap,
  Building,
  Users
} from 'lucide-react'

const skills = [
  {
    icon: Code,
    title: "Full-Stack Development",
    description: "Building end-to-end solutions that just work",
    bgColor: "bg-[#ffaf68]",
    rotateClass: "rotate-2"
  },
  {
    icon: Lightbulb,
    title: "Problem Solving",
    description: "Turning networking pain points into seamless experiences",
    bgColor: "bg-[#79d45e]",
    rotateClass: "-rotate-1"
  },
  {
    icon: Heart,
    title: "User-Centric Design",
    description: "Creating intuitive interfaces that people actually enjoy using",
    bgColor: "bg-[#f4889a]",
    rotateClass: "rotate-3"
  },
  {
    icon: Zap,
    title: "AI Integration",
    description: "Leveraging smart technology to make meaningful connections",
    bgColor: "bg-[#70b5ff]",
    rotateClass: "-rotate-2"
  }
]

const journey = [
  {
    title: "The Problem",
    description: "Experienced countless awkward networking events where meaningful connections were rare",
    bgColor: "bg-[#ba81ee]",
    rotateClass: "rotate-1"
  },
  {
    title: "The Vision",
    description: "Realized technology could solve this by intelligently matching people with common interests",
    bgColor: "bg-[#4928fd]",
    rotateClass: "-rotate-1"
  },
  {
    title: "The Solution",
    description: "Built SocialLoop to eliminate small talk and create genuine professional relationships",
    bgColor: "bg-[#ffaf68]",
    rotateClass: "rotate-2"
  },
  {
    title: "The Impact",
    description: "Now helping thousands of professionals make meaningful connections at events worldwide",
    bgColor: "bg-[#79d45e]",
    rotateClass: "-rotate-3"
  }
]

const transitionVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
}

export default function AboutPage() {
  return (
    <div className="w-full" style={{ backgroundColor: '#fdf1d3' }}>
      <HeroHeader />
      <main className="relative overflow-hidden">
        {/* Scattered decorative elements */}
        <div className="absolute top-20 left-10 w-8 h-8 bg-[#4928fd] border-4 border-black transform rotate-45 shadow-xl"></div>
        <div className="absolute top-40 right-20 w-6 h-6 bg-[#ba81ee] border-4 border-black rounded-full shadow-xl"></div>
        <div className="absolute bottom-32 left-1/4 w-5 h-12 bg-[#79d45e] border-4 border-black transform -rotate-12 shadow-xl"></div>

        {/* Floating sticky note */}
        <div className="absolute top-32 right-4 bg-[#f6e683] border-4 border-black p-3 transform rotate-12 shadow-xl max-w-32 hidden md:block">
          <p className="text-xs font-black text-black">"Making networking actually work!"</p>
        </div>

        {/* Hero Section */}
        <section className="relative px-6 py-16 md:py-32">
          <div className="mx-auto max-w-4xl text-center">
            <AnimatedGroup variants={{ item: transitionVariants }}>
              <div className="bg-[#4928fd] border-4 border-black p-3 inline-block transform -rotate-1 hover:rotate-0 transition-transform duration-200 mb-6">
                <Badge variant="outline" className="border-white text-white bg-transparent">
                  <User className="mr-2 h-4 w-4" />
                  MEET FAVOUR
                </Badge>
              </div>
            </AnimatedGroup>

            <div className="bg-[#ffaf68] border-4 border-black p-8 inline-block transform rotate-1 hover:rotate-0 transition-transform duration-200 mb-6">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-black leading-tight">
                HI, I'M FAVOUR<br />I MAKE NETWORKING<br />EASY
              </h1>
            </div>

            <div className="bg-white border-4 border-black p-6 inline-block transform -rotate-0.5 hover:rotate-0 transition-transform duration-200 max-w-3xl">
              <p className="text-lg font-semibold text-black">
                I'm a solo developer who got tired of awkward networking events and decided to build something better. 
                SocialLoop is my solution to making professional connections actually meaningful and enjoyable.
              </p>
            </div>
          </div>
        </section>

        {/* Personal Story */}
        <section className="relative px-6 py-16" style={{ backgroundColor: '#d9f3fc' }}>
          <div className="mx-auto max-w-6xl">
            <div className="grid gap-12 lg:grid-cols-2 items-center">
              <AnimatedGroup variants={{ item: transitionVariants }}>
                <div>
                  <div className="bg-[#79d45e] border-4 border-black p-6 transform rotate-1 hover:rotate-0 transition-transform duration-200 mb-6">
                    <h2 className="text-3xl md:text-4xl font-black text-black">My Story</h2>
                  </div>
                  
                  <div className="bg-white border-4 border-black p-6 transform -rotate-0.5 hover:rotate-0 transition-transform duration-200 mb-6">
                    <p className="text-lg font-semibold text-black mb-4">
                      As a developer, I've attended countless networking events, conferences, and meetups. 
                      Most of them were... well, pretty awkward. Random conversations about the weather, 
                      exchanging business cards that went nowhere, and missed opportunities to connect with the right people.
                    </p>
                    <p className="text-lg font-semibold text-black mb-4">
                      I realized that networking doesn't have to be this way. With the right technology, 
                      we can make genuine connections based on shared interests, goals, and complementary skills.
                    </p>
                    <p className="text-lg font-semibold text-black">
                      So I built SocialLoop - not just another networking app, but a comprehensive platform 
                      that makes networking fun, purposeful, and actually effective.
                    </p>
                  </div>
                </div>
              </AnimatedGroup>

              <AnimatedGroup variants={{ item: transitionVariants }}>
                <div className="space-y-4">
                  <div className="bg-[#ffaf68] border-4 border-black p-4 transform rotate-2 hover:rotate-0 transition-transform duration-200">
                    <div className="flex items-center gap-3">
                      <Coffee className="h-6 w-6 text-black" />
                      <span className="font-black text-black">Fueled by coffee and curiosity</span>
                    </div>
                  </div>
                  <div className="bg-[#70b5ff] border-4 border-black p-4 transform -rotate-1 hover:rotate-0 transition-transform duration-200">
                    <div className="flex items-center gap-3">
                      <Building className="h-6 w-6 text-black" />
                      <span className="font-black text-black">Solo developer, big dreams</span>
                    </div>
                  </div>
                  <div className="bg-[#ba81ee] border-4 border-black p-4 transform rotate-3 hover:rotate-0 transition-transform duration-200">
                    <div className="flex items-center gap-3">
                      <Users className="h-6 w-6 text-black" />
                      <span className="font-black text-black">50,000+ connections facilitated</span>
                    </div>
                  </div>
                  <div className="bg-[#f4889a] border-4 border-black p-4 transform -rotate-2 hover:rotate-0 transition-transform duration-200">
                    <div className="flex items-center gap-3">
                      <Target className="h-6 w-6 text-black" />
                      <span className="font-black text-black">Mission: End awkward networking forever</span>
                    </div>
                  </div>
                </div>
              </AnimatedGroup>
            </div>
          </div>
        </section>

        {/* Skills Section */}
        <section className="relative px-6 py-16">
          <div className="mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <AnimatedGroup variants={{ item: transitionVariants }}>
                <div className="bg-[#4928fd] border-4 border-black p-3 inline-block transform rotate-1 hover:rotate-0 transition-transform duration-200 mb-6">
                  <Badge variant="outline" className="border-white text-white bg-transparent">
                    <Code className="mr-2 h-4 w-4" />
                    WHAT I DO
                  </Badge>
                </div>
              </AnimatedGroup>
              
              <div className="bg-[#70b5ff] border-4 border-black p-6 inline-block transform -rotate-1 hover:rotate-0 transition-transform duration-200">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-black">
                  HOW I MAKE THINGS EASY
                </h2>
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {skills.map((skill, index) => (
                <AnimatedGroup
                  key={skill.title}
                  variants={{
                    item: {
                      ...transitionVariants,
                      visible: {
                        ...transitionVariants.visible,
                        transition: {
                          ...transitionVariants.visible.transition,
                          delay: index * 0.1
                        }
                      }
                    }
                  }}>
                  <div className={`${skill.bgColor} border-4 border-black p-6 transform hover:rotate-0 transition-all duration-200 shadow-xl ${skill.rotateClass} h-full text-center`}>
                    <div className="mx-auto mb-4 w-12 h-12 bg-white border-4 border-black rounded-full flex items-center justify-center">
                      <skill.icon className="h-6 w-6 text-black" />
                    </div>
                    <h3 className="text-lg font-black text-black mb-3">{skill.title}</h3>
                    <p className="text-black font-semibold text-sm">{skill.description}</p>
                    
                    {/* Decorative elements */}
                    <div className="absolute -top-2 -left-2 w-4 h-4 bg-white border-2 border-black transform rotate-45"></div>
                    <div className="absolute -bottom-2 -right-2 w-3 h-3 bg-black rounded-full"></div>
                  </div>
                </AnimatedGroup>
              ))}
            </div>
          </div>
        </section>

        {/* Journey Section */}
        <section className="relative px-6 py-16" style={{ backgroundColor: '#ba81ee' }}>
          <div className="mx-auto max-w-4xl">
            <div className="text-center mb-16">
              <AnimatedGroup variants={{ item: transitionVariants }}>
                <div className="bg-white border-4 border-black p-6 inline-block transform rotate-1 hover:rotate-0 transition-transform duration-200">
                  <h2 className="text-3xl md:text-4xl font-black text-black">
                    FROM PROBLEM TO SOLUTION
                  </h2>
                </div>
              </AnimatedGroup>
            </div>

            <div className="space-y-8">
              {journey.map((step, index) => (
                <AnimatedGroup
                  key={step.title}
                  variants={{
                    item: {
                      ...transitionVariants,
                      visible: {
                        ...transitionVariants.visible,
                        transition: {
                          ...transitionVariants.visible.transition,
                          delay: index * 0.1
                        }
                      }
                    }
                  }}>
                  <div className={`${step.bgColor} border-4 border-black p-6 transform hover:rotate-0 transition-all duration-200 shadow-xl ${step.rotateClass}`}>
                    <div className="flex items-center gap-6">
                      <div className="bg-white border-4 border-black p-3 font-black text-black text-xl min-w-fit">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-black text-black mb-2">{step.title}</h3>
                        <p className="text-black font-semibold">{step.description}</p>
                      </div>
                    </div>
                  </div>
                </AnimatedGroup>
              ))}
            </div>
          </div>
        </section>

        {/* Philosophy */}
        <section className="relative px-6 py-16">
          <div className="mx-auto max-w-4xl text-center">
            <AnimatedGroup variants={{ item: transitionVariants }}>
              <div className="bg-[#79d45e] border-4 border-black p-6 inline-block transform -rotate-1 hover:rotate-0 transition-transform duration-200 mb-6">
                <h2 className="text-3xl md:text-4xl font-black text-black">
                  My Philosophy
                </h2>
              </div>
              
              <div className="bg-white border-4 border-black p-8 inline-block transform rotate-0.5 hover:rotate-0 transition-transform duration-200 max-w-3xl">
                <p className="text-xl font-semibold text-black leading-relaxed mb-4">
                  "Technology should make human connections easier, not harder."
                </p>
                <p className="text-lg font-semibold text-black">
                  Every feature I build, every algorithm I write, and every design choice I make 
                  is guided by this simple principle. If it doesn't make networking more natural 
                  and meaningful, it doesn't belong in SocialLoop.
                </p>
              </div>
            </AnimatedGroup>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative px-6 py-16" style={{ backgroundColor: '#d9f3fc' }}>
          <div className="mx-auto max-w-4xl text-center">
            <AnimatedGroup variants={{ item: transitionVariants }}>
              <div className="bg-[#4928fd] border-4 border-black p-6 inline-block transform rotate-1 hover:rotate-0 transition-transform duration-200 mb-6">
                <h2 className="text-3xl md:text-4xl font-black text-white">
                  Let's Connect!
                </h2>
              </div>
              <div className="bg-white border-4 border-black p-4 inline-block transform -rotate-0.5 hover:rotate-0 transition-transform duration-200 mb-8">
                <p className="text-lg font-semibold text-black">
                  Want to see how SocialLoop can transform your next networking event? 
                  Let's make meaningful connections happen.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <div className="bg-[#79d45e] border-4 border-black transform rotate-2 hover:rotate-0 transition-transform duration-200">
                  <Button size="lg" className="bg-[#79d45e] hover:bg-[#79d45e]/90 text-black border-0 font-black" asChild>
                    <Link href="/signup">
                      Try SocialLoop Free
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
                <div className="bg-white border-4 border-black transform -rotate-1 hover:rotate-0 transition-transform duration-200">
                  <Button size="lg" className="bg-white hover:bg-gray-50 text-black border-0 font-black" asChild>
                    <Link href="/contact">
                      Get In Touch
                    </Link>
                  </Button>
                </div>
              </div>
            </AnimatedGroup>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  )
}
